import React from 'react';
import {
  useInvalidateQueries,
  useUpdateRecord,
  useCreateRecord,
} from 'src/api/APIHooks';
import { defaultMutationConfig } from 'common/utils/queryDefaults';
import PropTypes from 'prop-types';
import { Form, Field } from 'react-final-form';
import { Button, MultiSelectField } from '@unite-us/app-components';

export const useUpdateCase = () => {
  const invalidateQueries = useInvalidateQueries();
  return useUpdateRecord('case', {
    mutationConfig: {
      onSuccess: () => {
        invalidateQueries('cases');
      },
    },
  });
};

const useCreateNote = () => {
  const invalidateQueries = useInvalidateQueries();
  return useCreateRecord('note', {
    mutationConfig: {
      onSuccess: () => {
        invalidateQueries('notes');
      },
      ...defaultMutationConfig,
    },
  });
};

const ClientNeedForm = ({
  closeModal,
  selectedConditionIds,
  conditions,
  caseId,
  modalIsOpen,
  reloadCase,
  currentEmployeeId,
}) => {
  const { updateRecord: updateCase } = useUpdateCase();
  const { createRecord: createNote } = useCreateNote();

  const personConditions = conditions.map((condition) => ({
    label: condition.name,
    value: condition.id,
  }));

  const onSubmit = async (formInputs) => {
    const newSelectedIds = formInputs.client_needs || [];
    const hasChanges = JSON.stringify(selectedConditionIds.sort()) !== JSON.stringify(newSelectedIds.sort());

    if (hasChanges) {
      const originalClientNeeds = selectedConditionIds
        .map((id) => conditions.find((condition) => condition.id === id)?.name)
        .join(', ');
      const newClientNeeds = newSelectedIds
        .map((id) => conditions.find((condition) => condition.id === id)?.name)
        .join(', ');

      await Promise.all([
        updateCase(caseId, {
          person_conditions: newSelectedIds.map((id) => ({
            id,
            type: 'person_condition',
          })),
        }),
        createNote({
          text: `Associated needs changed from ${originalClientNeeds} to ${newClientNeeds}`,
          subjectType: 'case',
          subject: caseId,
          employee: currentEmployeeId,
          category: 'general',
          details: { deletable: false, type: 'update' },
        }),
      ]).then(() => {
        reloadCase();
      });
    }
    closeModal();
  };

  if (!modalIsOpen) {
    return null;
  }

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={{ client_needs: selectedConditionIds }}
      render={({ handleSubmit, submitting, values }) => {
        const isSubmitDisabled = submitting || !values.client_needs || values.client_needs.length === 0;

        return (
          <form className="mt-4" onSubmit={handleSubmit}>
            <div style={{ marginBottom: '115px' }}>
              <Field
                name="client_needs"
                validate={(value) => {
                  if (!value || value.length === 0) {
                    return 'Required';
                  }
                  return undefined;
                }}
                render={({ input, meta }) => (
                  <MultiSelectField
                    id="service-case-reason"
                    label="Associated Needs"
                    options={personConditions}
                    value={input.value || []}
                    onChange={input.onChange}
                    onBlur={input.onBlur}
                    required
                    placeholder="Pick One or More"
                    clearable
                    error={meta.touched && meta.error}
                  />
                )}
              />
            </div>
            <div className="actions">
              <span className="action-item">
                <Button
                  id="description-cancel-btn"
                  label="Cancel"
                  onClick={closeModal}
                  disabled={submitting}
                />
              </span>
              <span className="action-item">
                <Button
                  id="description-submit-btn"
                  onClick={handleSubmit}
                  primary
                  disabled={isSubmitDisabled}
                  label="Submit"
                />
              </span>
            </div>
          </form>
        );
      }}
    />
  );
};

ClientNeedForm.propTypes = {
  closeModal: PropTypes.func.isRequired,
  selectedConditionIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  conditions: PropTypes.array.isRequired,
  caseId: PropTypes.string.isRequired,
  modalIsOpen: PropTypes.bool.isRequired,
  reloadCase: PropTypes.func.isRequired,
  currentEmployeeId: PropTypes.string.isRequired,
};

ClientNeedForm.contextTypes = {
  eventTracker: PropTypes.func.isRequired,
};

export default ClientNeedForm;
